import React, { useState, useEffect } from 'react';
import { leadsService } from '../../services/leads';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Assignment, Person } from '@mui/icons-material';

const AssignLeadDialog = ({ open, onClose, onSubmit, lead }) => {
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedUser('');
      setReason('');
      setError('');
      setAssignableUsers([]);
    }
  }, [open]);

  useEffect(() => {
    if (open && lead?.id) {
      fetchAssignableUsers();
    }
  }, [open, lead?.id]);

  const fetchAssignableUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await leadsService.getAssignableUsers(lead.id);
      setAssignableUsers(data.assignable_users || []);
    } catch (err) {
      console.error('Error fetching assignable users:', err);
      setError('Failed to load assignable users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('AssignLeadDialog - handleSubmit called');
    console.log('Selected user:', selectedUser);
    console.log('Reason:', reason);
    console.log('Available users:', assignableUsers);
    
    if (!selectedUser) {
      console.warn('No user selected');
      return;
    }

    const submissionData = {
      assigned_to: selectedUser,
      reason: reason.trim()
    };
    
    console.log('Submitting assignment data:', submissionData);
    onSubmit(submissionData);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'primary',
      'contacted': 'info', 
      'qualified': 'warning',
      'proposal': 'secondary',
      'won': 'success',
      'lost': 'error'
    };
    return colors[status] || 'default';
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Assignment sx={{ mr: 1 }} />
          Assign Lead
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {lead && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Lead Information
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2, 
                backgroundColor: 'grey.50',
                borderRadius: 1 
              }}>
                <Avatar sx={{ mr: 2 }}>
                  {(lead.name?.[0] || lead.first_name?.[0] || '?')}{(lead.last_name?.[0] || lead.name?.[1] || '')}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1">
                    {lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown Lead'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[lead.company, lead.email].filter(Boolean).join(' • ') || 'No contact info'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={lead.status} 
                      color={getStatusColor(lead.status)}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel>Assign To</InputLabel>
            <Select
              value={selectedUser}
              label="Assign To"
              onChange={(e) => setSelectedUser(e.target.value)}
              disabled={loading}
            >
              {loading ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading assignable users...</Typography>
                  </Box>
                </MenuItem>
              ) : (
                assignableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1, fontSize: 18 }} />
                        <Box>
                          <Typography variant="body2">
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.role.toUpperCase()} • {user.username}
                          </Typography>
                        </Box>
                      </Box>
                      {!user.lead_flow_enabled && (
                        <Chip 
                          label="Flow Paused" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Reason for Assignment (Optional)"
            multiline
            rows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for this assignment..."
            sx={{ mb: 2 }}
          />

          {!loading && assignableUsers.length === 0 && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'warning.light', 
              borderRadius: 1,
              color: 'warning.contrastText'
            }}>
              <Typography variant="body2">
                No users available for assignment to this lead. This could be due to territory, disease specialization, or lead flow restrictions.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading || !selectedUser || assignableUsers.length === 0}
            startIcon={loading ? <CircularProgress size={16} /> : <Assignment />}
          >
            {loading ? 'Loading...' : 'Assign Lead'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignLeadDialog;